/* ==========================================================================
   ProTrade Commerce — predictive-search.js
   Debounced Shopify predictive search with keyboard navigation.
   ========================================================================== */
(function () {
  'use strict';

  const T = window.theme;
  const { $, $$, debounce } = T.utils;

  const cache = new Map();

  class PredictiveSearch {
    constructor(root) {
      this.root = root;
      this.input = $('input[type="search"]', root);
      this.results = $('[data-predictive-results]', root);
      this.sectionId = root.dataset.predictiveSection || 'predictive-search';
      if (!this.input || !this.results) return;

      this.selectedIndex = -1;
      this.controller = null;
      this.bind();
    }

    bind() {
      this.input.addEventListener('input', debounce(() => this.onInput(), 260));
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim().length >= 2) this.open();
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));

      document.addEventListener('click', (event) => {
        if (!this.root.contains(event.target)) this.close();
      });
    }

    onInput() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.close();
        return;
      }
      this.search(term);
    }

    onKeydown(event) {
      const items = $$('[data-predictive-item]', this.results);

      switch (event.key) {
        case 'ArrowDown':
          if (!items.length) return;
          event.preventDefault();
          this.select(Math.min(this.selectedIndex + 1, items.length - 1), items);
          break;
        case 'ArrowUp':
          if (!items.length) return;
          event.preventDefault();
          this.select(Math.max(this.selectedIndex - 1, -1), items);
          break;
        case 'Enter':
          if (this.selectedIndex > -1 && items[this.selectedIndex]) {
            event.preventDefault();
            items[this.selectedIndex].click();
          }
          break;
        case 'Escape':
          this.close();
          break;
        default:
          break;
      }
    }

    select(index, items) {
      this.selectedIndex = index;
      items.forEach((item, i) => item.setAttribute('aria-selected', String(i === index)));
      if (index > -1 && items[index]) {
        items[index].scrollIntoView({ block: 'nearest' });
        this.input.setAttribute('aria-activedescendant', items[index].id || '');
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    async search(term) {
      const key = term.toLowerCase();
      if (cache.has(key)) {
        this.render(cache.get(key));
        return;
      }

      this.renderLoading();

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        section_id: this.sectionId
      });

      try {
        const response = await fetch(`${T.routes.predictiveSearch}?${params}`, {
          signal: this.controller.signal,
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Search failed');

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('[data-predictive-inner]');
        const markup = fresh ? fresh.innerHTML : '';

        cache.set(key, markup);
        if (cache.size > 40) cache.delete(cache.keys().next().value);
        this.render(markup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.results.innerHTML = `<div class="predictive-search__empty">${T.strings.cartError}</div>`;
        this.open();
      }
    }

    renderLoading() {
      this.results.innerHTML =
        '<div class="predictive-search__loading" role="status"><span class="spinner"></span></div>';
      this.open();
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.selectedIndex = -1;
      this.open();
      $$('[data-predictive-item]', this.results).forEach((item, index) => {
        item.id = item.id || `predictive-item-${index}`;
      });
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.results.hidden = true;
      this.selectedIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  function init(scope) {
    $$('[data-predictive-search]', scope || document).forEach((root) => {
      if (root._predictive) return;
      root._predictive = new PredictiveSearch(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('theme:section:load', (event) => init(event.detail.target));
})();
